import type { ComponentProps, ReactNode, Ref } from "react";

import {
  SESSION_SCREEN_MAIN_CLASS,
  SESSION_TRANSCRIPT_COLUMN_CLASS,
  SESSION_TRANSCRIPT_FRAME_CLASS,
  SessionAnswerFooter,
  SessionComposerFrame,
  SessionDetailsButton,
  SessionToolbar,
} from "@product/app/workspace/sessions/[sessionId]/session-screen-view";
import { RunTracePill, RunTraceRow } from "@product/components/app/runs/run-trace-pill";
import { cn } from "@v1/ui/utils";

import { AppSurface, type AppThemeTone } from "@/components/editorial/product-bridge/app-surface";
import { ProductLinkSandbox } from "@/components/editorial/product-bridge/product-link-sandbox";

import { ShotComposer, ShotMenubar, ShotRail } from "./chrome";
import { RUN } from "./fixture-run";
import { FrameContents } from "./frame-contents";
import { OSTREY_WORKSPACE, type ShotWorkspace } from "./workspace";

/**
 * The window a product shot is framed in: the application at a fixed size,
 * cropped rather than scaled, so every pixel is the product's own at 1:1
 * (from `sm`; a phone draws it a little smaller, see FRAME_CONTENTS_CLS).
 * Heights are reserved per breakpoint, so the frame never shifts the page.
 * Every product frame on a page shares this height. From `sm` it fits the
 * record sheet whole for the longest story record (ten fields), so a replay
 * that opens a record never cuts its last field.
 */
export const SHOT_WINDOW_HEIGHT_CLASS = "h-[680px] sm:h-[856px]";

/**
 * What a frame holds, sized to the frame; on a phone, drawn at 88%.
 *
 * On a phone a frame keeps the page's gutter and its own border on every
 * side: run edge to edge, the app's menu bar sat under the site's header
 * and read as the site's own (Archie, 2026-10-02). That leaves the app 42px
 * less than the screen, and it is not built for a window that narrow (the
 * composer's controls collide under 360px). So the phone is the one place a
 * shot is scaled: the app is laid out 1/0.88 times as wide and as tall as
 * the frame, and drawn at 0.88, which on any phone from 350px wide gives it
 * at least the screen's own width, as it had edge to edge.
 *
 * Scripts that measure inside a frame see boxes as drawn, so a distance
 * taken from them is 88% of the layout's on a phone (scroll-offset.ts).
 */
const FRAME_CONTENTS_CLS =
  "relative flex min-h-0 min-w-0 flex-1 flex-col max-sm:h-[calc(100%/0.88)] max-sm:w-[calc(100%/0.88)] max-sm:flex-none max-sm:origin-top-left max-sm:scale-[0.88]";

/**
 * The closed run trace as TaskRunActivity renders it: the phone pill and
 * the desktop pill, one shown per breakpoint. Server and client shots share
 * it; only an interactive shot passes `onOpen`.
 */
export function ShotClosedTrace({
  onOpen,
  label = RUN.label,
  updateCountLabel = RUN.updateCountLabel,
  durationLabel = RUN.durationLabel,
  running = false,
}: {
  onOpen?: () => void;
  label?: string;
  updateCountLabel?: string;
  durationLabel?: string | null;
  running?: boolean;
} = {}) {
  const pill = (className: string) => (
    <RunTracePill
      label={label}
      updateCountLabel={updateCountLabel}
      durationLabel={durationLabel}
      running={running}
      onClick={onOpen}
      className={className}
    />
  );
  return (
    <RunTraceRow className="w-auto min-w-0 max-w-none flex-auto">
      {pill("lg:hidden")}
      {pill("hidden lg:inline-flex")}
    </RunTraceRow>
  );
}

/** The trace above the composer, and the feedback prompt once the run has finished. */
export function ShotAnswerFooter({
  trace = <ShotClosedTrace />,
  showHelpImprove = true,
}: { trace?: ReactNode; showHelpImprove?: boolean } = {}) {
  return <SessionAnswerFooter activity={trace} showHelpImprove={showHelpImprove} />;
}

/**
 * The application shell a shot is framed in: the menu bar, the workspace
 * rail and the pane beside it, at a fixed size, cropped rather than scaled.
 * The frame's border and radius are the only marketing additions. The rail
 * appears once the frame itself is 1024px wide, where the app shows it, so
 * a frame narrower than the page renders as the app would in a window that
 * size. Without a rail, the pane takes the window below the menu bar, as a
 * session pane at its Full preset covers the rail. With `chromeInert`, the
 * menu bar and rail render as they are but take no pointer, focus or
 * assistive technology.
 *
 * The frame is served at its size and empty: what it holds is drawn in the
 * browser as it nears the viewport (frame-contents). So where a shot says
 * its first frame is rendered on the server, the server renders the window
 * and holds its contents back; the page's HTML carries none of the sample
 * workspace. A test that checks what a frame holds renders it filled
 * (filled-markup).
 */
export function ShotAppFrame({
  tone = "light",
  workspace,
  rail,
  children,
  overlay,
  inboxBadge,
  chromeInert = false,
  className,
  heightClass = SHOT_WINDOW_HEIGHT_CLASS,
  ...props
}: {
  tone?: AppThemeTone;
  workspace: ShotWorkspace;
  /** The workspace rail; none when the pane covers it. */
  rail?: ReactNode;
  /** The pane: a session, a channel or the Inbox screen, or a session pane over the window. */
  children: ReactNode;
  /** Drawn over the whole window, as the app's sheets are, e.g. a record sheet. */
  overlay?: ReactNode;
  /** The menu bar's Inbox count, when Inbox items need attention. */
  inboxBadge?: ReactNode;
  chromeInert?: boolean;
  className?: string;
  heightClass?: string;
} & Omit<ComponentProps<"div">, "children">) {
  const chrome = chromeInert ? { inert: true } : {};
  return (
    <AppSurface
      tone={tone}
      className={cn(
        "@container relative flex flex-col overflow-hidden rounded-lg border border-border shadow-[0_1px_2px_rgb(0_0_0/0.04),0_24px_48px_-24px_rgb(0_0_0/0.18)]",
        heightClass,
        className,
      )}
      // The window's outer edge keeps its slight radius on a page that squares its corners (one-system).
      data-frame-corners=""
      // The sample workspace's words never make a search result's snippet.
      data-nosnippet=""
      {...props}
    >
      {/* The window is served at its size and empty; its contents are drawn
          in the browser as it nears the viewport (frame-contents). */}
      <FrameContents>
        <ProductLinkSandbox>
          <div className={FRAME_CONTENTS_CLS}>
            <div className="contents" {...chrome}>
              <ShotMenubar workspace={workspace} inboxBadge={inboxBadge} />
            </div>
            <div className="relative flex min-h-0 flex-1 overflow-hidden">
              {rail == null ? null : (
                <>
                  <div
                    className="hidden w-[21%] min-w-[240px] max-w-[480px] shrink-0 @min-[1024px]:block"
                    {...chrome}
                  >
                    {rail}
                  </div>
                  {/* The workspace shell's resize handle, at rest. */}
                  <div aria-hidden="true" className="hidden w-px shrink-0 bg-border/60 @min-[1024px]:block" />
                </>
              )}
              {children}
            </div>
            {overlay}
          </div>
        </ProductLinkSandbox>
      </FrameContents>
    </AppSurface>
  );
}

/**
 * The application shell around a session: the session pane with its
 * toolbar, transcript and composer, in ShotAppFrame. Everything inside is the
 * product's own views over fixture data.
 *
 * Interactive shots set `chromeInert`: the menu bar, rail, toolbar and
 * composer then render as they are but take no pointer, focus or assistive
 * technology, leaving only the transcript and its trace to explore.
 */
export function ShotWindow({
  tone = "light",
  workspace = OSTREY_WORKSPACE,
  title = workspace.sessionTitle,
  rail = <ShotRail workspace={workspace} />,
  transcript,
  footer = <ShotAnswerFooter />,
  composer = <ShotComposer sources={workspace.composerSources} />,
  overlay,
  chromeInert = false,
  anchor = "start",
  className,
  heightClass = SHOT_WINDOW_HEIGHT_CLASS,
  transcriptRef,
  ...props
}: {
  tone?: AppThemeTone;
  /** The workspace the frame shows: its menu bar, rail and composer sources. */
  workspace?: ShotWorkspace;
  title?: string;
  rail?: ReactNode;
  transcript: ReactNode;
  footer?: ReactNode;
  composer?: ReactNode;
  /** Drawn over the whole window, as the app's sheets are, e.g. a record sheet. */
  overlay?: ReactNode;
  chromeInert?: boolean;
  /**
   * Where the transcript rests. `end` is where the app leaves a session it
   * opens (following the live edge): at the bottom when the turn overflows
   * the pane, at the top when it fits. Drawn in CSS (a reversed column whose
   * spare space sits below the turn), so a server-rendered frame rests
   * there without script.
   */
  anchor?: "start" | "end";
  className?: string;
  heightClass?: string;
  transcriptRef?: Ref<HTMLElement>;
} & Omit<ComponentProps<"div">, "children">) {
  const chrome = chromeInert ? { inert: true } : {};
  return (
    <ShotAppFrame
      tone={tone}
      workspace={workspace}
      rail={rail}
      overlay={overlay}
      chromeInert={chromeInert}
      className={className}
      heightClass={heightClass}
      {...props}
    >
      <main className={SESSION_SCREEN_MAIN_CLASS}>
        <div className="contents" {...chrome}>
          <SessionToolbar
            title={title}
            titleText={title}
            actions={<SessionDetailsButton active={false} />}
          />
        </div>
        <div className="relative min-h-0 flex-1">
          <section
            ref={transcriptRef}
            className={cn(SESSION_TRANSCRIPT_FRAME_CLASS, anchor === "end" && "flex flex-col-reverse")}
          >
            <div className={cn(SESSION_TRANSCRIPT_COLUMN_CLASS, anchor === "end" && "mb-auto")}>
              {/* The transcript list's row padding, then its bottom anchor
                  (session-transcript-virtual-list, session-screen). */}
              <div className="pb-9">{transcript}</div>
              <div className="h-px" aria-hidden />
            </div>
          </section>
        </div>
        <SessionComposerFrame>
          {footer}
          <div className="contents" {...chrome}>
            {composer}
          </div>
        </SessionComposerFrame>
      </main>
    </ShotAppFrame>
  );
}
